import React from 'react';
import cn from 'classnames';

import GRAPHICS_TAB_TEAM from 'assets/slickpay-splash/time-off/graphics-tab-one.png';
import GRAPHICS_TAB_HR from 'assets/slickpay-splash/time-off/graphics-tab-two.svg';
import ICON_TAB_HR from 'assets/slickpay-splash/time-off/hr-ico.svg';
import ICON_TAB_PAY from 'assets/slickpay-splash/time-off/pay-ico.svg';
import ICON_TAB_TEAM from 'assets/slickpay-splash/time-off/team-ico.svg';
import { HR_TAB_PAYROLL_IMAGES } from 'constants/images/hrManagement.const';
import { useCountryImage } from 'hooks/useCountryImage.hook';
import useStateWhileMounted from 'hooks/useStateWhileMounted.hook';
import { useTranslation } from 'providers/GILT.provider';
import createNodeKey from 'utils/react/createNodeKey.util';
import C from 'utils/values/constant.util';

import CN from './TimeOffSplashTabs.module.scss';

const TimeOffSplashTeamTab = ({ titleTrKey }) => {
  const { t } = useTranslation();
  return (
    <div className={CN.tabPanel} data-bem="TimeOffSplashTeamTab">
      <div className={CN.text}>
        <div className={CN.title}>{t(titleTrKey)}</div>
        <div className={CN.block}>
          <div className={CN.blockTitle}>{t('humanResources.splashOverview.teamSectionSubTitle1')}</div>
          <div className={CN.blockText}>{t('humanResources.splashOverview.teamSectionDescription1')}</div>
        </div>
        <div className={CN.block}>
          <div className={CN.blockTitle}>{t('humanResources.splashOverview.teamSectionSubTitle2')}</div>
          <div className={CN.blockText}>{t('humanResources.splashOverview.teamSectionDescription2')}</div>
        </div>
      </div>
      <div className={CN.graphics}>
        <img src={GRAPHICS_TAB_TEAM} alt={t(titleTrKey)} className={CN.illustration} />
      </div>
    </div>
  );
};

const TimeOffSplashManagementTab = ({ titleTrKey }) => {
  const { t } = useTranslation();
  return (
    <div className={CN.tabPanel} data-bem="TimeOffSplashManagementTab">
      <div className={CN.text}>
        <div className={CN.title}>{t(titleTrKey)}</div>
        <div className={CN.block}>
          <div className={CN.blockTitle}>{t('humanResources.splashOverview.hrSectionSubTitle1')}</div>
          <div className={CN.blockText}>{t('humanResources.splashOverview.hrSectionDescription1')}</div>
        </div>
        <div className={CN.block}>
          <div className={CN.blockTitle}>{t('humanResources.splashOverview.hrSectionSubTitle2')}</div>
          <div className={CN.blockText}>{t('humanResources.splashOverview.hrSectionDescription2')}</div>
        </div>
      </div>
      <div className={CN.graphics}>
        <img src={GRAPHICS_TAB_HR} alt={t(titleTrKey)} className={CN.illustration} />
      </div>
    </div>
  );
};

const TimeOffSplashPayrollTab = ({ titleTrKey }) => {
  const { t } = useTranslation();

  const payrollGraphic = useCountryImage(HR_TAB_PAYROLL_IMAGES);

  return (
    <div className={CN.tabPanel} data-bem="TimeOffSplashPayrollTab">
      <div className={CN.text}>
        <div className={CN.title}>{t(titleTrKey)}</div>
        <div className={CN.block}>
          <div className={CN.blockTitle}>{t('humanResources.splashOverview.payrollSectionSubTitle1')}</div>
          <div className={CN.blockText}>{t('humanResources.splashOverview.payrollSectionDescription1')}</div>
        </div>
        <div className={CN.block}>
          <div className={CN.blockTitle}>{t('humanResources.splashOverview.payrollSectionSubTitle2')}</div>
          <div className={CN.blockText}>{t('humanResources.splashOverview.payrollSectionDescription2')}</div>
        </div>
      </div>
      <div className={CN.graphics}>
        <img src={payrollGraphic} alt={t(titleTrKey)} className={CN.illustration} />
      </div>
    </div>
  );
};

const TAB = Object.freeze({
  team: 'team',
  hr: 'hr',
  pay: 'payment',
});

const COMPONENT = Object.freeze({
  [TAB.team]: <TimeOffSplashTeamTab titleTrKey="humanResources.splashOverview.teamManagement" />,
  [TAB.hr]: <TimeOffSplashManagementTab titleTrKey="humanResources.splashOverview.hrManagement" />,
  [TAB.pay]: <TimeOffSplashPayrollTab titleTrKey="humanResources.splashOverview.payroll" />,
});

const INFO = C({
  [TAB.team]: {
    headingTrKey: 'humanResources.splashOverview.teamManagement',
    headingMobileTrKey: 'humanResources.splashOverview.team',
    icon: ICON_TAB_TEAM,
  },
  [TAB.hr]: {
    headingTrKey: 'humanResources.splashOverview.hrManagement',
    headingMobileTrKey: 'humanResources.splashOverview.hr',
    icon: ICON_TAB_HR,
  },
  [TAB.pay]: {
    headingTrKey: 'humanResources.splashOverview.payroll',
    headingMobileTrKey: 'humanResources.splashOverview.payroll',
    icon: ICON_TAB_PAY,
  },
});

const TimeOffSplashTabs = () => {
  const { t } = useTranslation();
  const [selected, setSelected] = useStateWhileMounted(TAB.team);

  return (
    <div className={CN.container} data-bem="TimeOffSplashTabs">
      <div className={CN.tabList} data-bem="TimeOffSplashTabs__tabList">
        {Object.entries(INFO).map(([tag, { headingTrKey, headingMobileTrKey, icon }], index) => (
          <div
            data-bem="TimeOffSplashTabs__tab"
            role="button"
            tabIndex={0}
            key={createNodeKey({ index, tag })}
            className={cn({
              [CN.tab]: true,
              [CN.activeTab]: selected === tag,
            })}
            onClick={() => void setSelected(tag)}
          >
            <img src={icon} alt={t(headingTrKey)} data-bem="TimeOffSplashTabs__image" />
            <div className={CN.tabTitle} data-bem="TimeOffSplashTabs__tabTitle">
              <span className={CN.normalTitle}>{t(headingTrKey)}</span>
              <span className={CN.mobileTitle}>{t(headingMobileTrKey)}</span>
            </div>
          </div>
        ))}
      </div>

      <div className={CN.tabsPanel} data-bem="TimeOffSplashTabs__tabsPanel">
        {COMPONENT[selected]}
      </div>
    </div>
  );
};

export default TimeOffSplashTabs;
